import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import {
 Button,
 TextField,
 Typography,
 Container,
 Box,
 Snackbar,
 Alert,
 Paper,
 FormControlLabel,
 Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";


const StyledContainer = styled(Container)(({ theme }) => ({
 minHeight: "100vh",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
}));


const StyledPaper = styled(Paper)(({ theme }) => ({
 padding: theme.spacing(4),
 width: "100%",
 maxWidth: 400,
 backgroundColor: "rgba(255, 255, 255, 0.95)",
 borderRadius: theme.spacing(2),
 boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
}));


const StyledButton = styled(Button)(({ theme }) => ({
 marginTop: theme.spacing(2),
 padding: theme.spacing(1.5),
 borderRadius: theme.spacing(1),
}));


const Signup = () => {
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [isProducer, setIsProducer] = useState(false);
 const [error, setError] = useState("");
 const [openSnackbar, setOpenSnackbar] = useState(false);
 const navigate = useNavigate();


 const handleSignup = async (e) => {
   e.preventDefault();
   try {
     const userCredential = await createUserWithEmailAndPassword(auth, email, password);
     const user = userCredential.user;


     // Store additional user data in Firestore
     await setDoc(doc(db, "users", user.uid), {
      email: email,
      isProducer: isProducer ? 1 : 0,
      role: "user", // Explicitly setting the role as "user"
      createdAt: new Date(),
    });
    

     setOpenSnackbar(true);
     navigate("/"); // Redirect to login after signup
   } catch (error) {
     switch (error.code) {
       case "auth/email-already-in-use":
         setError("This email is already registered.");
         break;
       case "auth/invalid-email":
         setError("Invalid email format.");
         break;
       case "auth/weak-password":
         setError("Password should be at least 6 characters.");
         break;
       default:
         setError("An error occurred. Please try again.");
     }
     setOpenSnackbar(true);
   }
 };


 const handleCloseSnackbar = () => {
   setOpenSnackbar(false);
 };


 return (
   <StyledContainer>
     <StyledPaper elevation={3}>
       <Box sx={{ textAlign: "center", mb: 3 }}>
         <Typography variant="h4" component="h1" gutterBottom sx={{ color: "#7d1aed" }}>
           Sign Up
         </Typography>
         <Typography variant="body2" color="text.secondary">
           Create your account to get started
         </Typography>
       </Box>
       <form onSubmit={handleSignup}>
         <TextField
           fullWidth
           variant="outlined"
           label="Email"
           type="email"
           value={email}
           onChange={(e) => setEmail(e.target.value)}
           required
           margin="normal"
           sx={{
             "& .MuiOutlinedInput-root": {
               "& fieldset": { borderColor: "#7d1aed" },
               "&:hover fieldset": { borderColor: "#ff4081" },
             },
           }}
         />
         <TextField
           fullWidth
           variant="outlined"
           label="Password"
           type="password"
           value={password}
           onChange={(e) => setPassword(e.target.value)}
           required
           margin="normal"
           sx={{
             "& .MuiOutlinedInput-root": {
               "& fieldset": { borderColor: "#7d1aed" },
               "&:hover fieldset": { borderColor: "#ff4081" },
             },
           }}
         />
         <FormControlLabel
           control={
             <Checkbox
               checked={isProducer}
               onChange={(e) => setIsProducer(e.target.checked)}
               sx={{
                 color: "#7d1aed",
                 "&.Mui-checked": { color: "#ff4081" },
               }}
             />
           }
           label="Sign up as Producer"
           sx={{ mt: 1, mb: 2 }}
         />
         <StyledButton
           type="submit"
           variant="contained"
           color="primary"
           fullWidth
         >
           Sign Up
         </StyledButton>
       </form>
     </StyledPaper>


     <Snackbar
       open={openSnackbar}
       autoHideDuration={6000}
       onClose={handleCloseSnackbar}
       anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
     >
       <Alert
         onClose={handleCloseSnackbar}
         severity={error ? "error" : "success"}
         sx={{ width: "100%" }}
       >
         {error || "Account created successfully! Please login."}
       </Alert>
     </Snackbar>
   </StyledContainer>
 );
};


export default Signup;
